import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { gql } from '@apollo/client'
import { apolloClient } from '../graphql/client'
import logger from '../utils/logger'
import { api } from './api'
import type { User } from '../types'

// GraphQL Mutations
const LOGIN_MUTATION = gql`
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      user {
        id
        globalId
        email
        username
        slug
        displayName
        emailVerified
        isAdmin
        createdAt
        updatedAt
      }
      token
    }
  }
`

const REGISTER_MUTATION = gql`
  mutation Register($input: RegisterInput!) {
    register(input: $input) {
      user {
        id
        globalId
        email
        username
        slug
        displayName
        emailVerified
        isAdmin
        createdAt
        updatedAt
      }
      token
    }
  }
`

const FORGOT_PASSWORD_MUTATION = gql`
  mutation ForgotPassword($input: ForgotPasswordInput!) {
    forgotPassword(input: $input) {
      success
      message
    }
  }
`

const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword($input: ResetPasswordInput!) {
    resetPassword(input: $input) {
      success
      message
    }
  }
`

interface AuthContextType {
  user: User | null
  login: (email: string, password: string) => Promise<void>
  register: (email: string, username: string, displayName: string, password: string) => Promise<void>
  logout: () => Promise<void>
  isLoading: boolean
  isAdmin: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchUserFromAPI = async () => {
      const token = localStorage.getItem('token')
      if (token) {
        try {
          // Fetch fresh user data including isAdmin flag (FR-017)
          const response = await api.get('/api/auth/me')
          const userData = response.data
          setUser(userData)
          localStorage.setItem('user', JSON.stringify(userData))
        } catch (error) {
          // Token invalid or expired - clear auth
          logger.error('AuthService', 'Failed to fetch user data:', error)
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          setUser(null)
        }
      }
      setIsLoading(false)
    }

    fetchUserFromAPI()
  }, [])

  const login = async (email: string, password: string) => {
    logger.info('Auth', 'Logging in via GraphQL', { email })

    const result = await apolloClient.mutate({
      mutation: LOGIN_MUTATION,
      variables: { input: { email, password } },
    })

    if (result.data?.login) {
      const { token, user: userData } = result.data.login
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(userData))
      setUser(userData)

      // Reset Apollo cache on login
      await apolloClient.resetStore()

      logger.info('Auth', 'Login successful', { userId: userData.id })
    }
  }

  const register = async (email: string, username: string, displayName: string, password: string) => {
    logger.info('Auth', 'Registering via GraphQL', { username })

    const result = await apolloClient.mutate({
      mutation: REGISTER_MUTATION,
      variables: {
        input: { email, username, displayName, password }
      },
    })

    if (result.data?.register) {
      const { token, user: userData } = result.data.register
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(userData))
      setUser(userData)

      // Reset Apollo cache on register
      await apolloClient.resetStore()

      logger.info('Auth', 'Registration successful', { userId: userData.id })
    }
  }

  const logout = async () => {
    // Clear HttpOnly auth cookie via server endpoint
    // This is required because HttpOnly cookies can't be cleared by JavaScript
    try {
      await api.post('/api/auth/logout')
      logger.info('Auth', 'Server logout successful (cookie cleared)')
    } catch (error) {
      // Continue with client-side cleanup even if server call fails
      logger.warn('Auth', 'Server logout failed, continuing with client cleanup', error)
    }

    // Clear client-side storage
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    setUser(null)

    // Use browser redirect instead of navigate() to ensure complete React state cleanup.
    // This full page reload guarantees all component state, contexts, and cached data are cleared.
    // Consistent with 401 error handler in api.ts.
    window.location.href = '/login'
  }

  const isAdmin = user?.isAdmin === true

  return (
    <AuthContext.Provider value={{ user, login, register, logout, isLoading, isAdmin }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Password Reset API Functions

export interface PasswordResetResponse {
  success: boolean
  message: string
  errorCode?: string
}

export async function requestPasswordReset(email: string): Promise<PasswordResetResponse> {
  logger.info('Auth', 'Requesting password reset via GraphQL', { email })

  const result = await apolloClient.mutate({
    mutation: FORGOT_PASSWORD_MUTATION,
    variables: { input: { email } },
  })

  if (result.data?.forgotPassword) {
    return {
      success: result.data.forgotPassword.success,
      message: result.data.forgotPassword.message || 'Reset email sent',
    }
  }

  return {
    success: false,
    message: 'Failed to send reset email',
  }
}

export async function resetPassword(token: string, newPassword: string): Promise<PasswordResetResponse> {
  logger.info('Auth', 'Resetting password via GraphQL')

  const result = await apolloClient.mutate({
    mutation: RESET_PASSWORD_MUTATION,
    variables: { input: { token, password: newPassword } },
  })

  if (result.data?.resetPassword) {
    return {
      success: result.data.resetPassword.success,
      message: result.data.resetPassword.message || 'Password reset successful',
    }
  }

  return {
    success: false,
    message: 'Failed to reset password',
  }
}
